import { CompanyConstant } from "presentation/constant/Company/CompanyConstant";
import { useCompanyDetailVM } from "presentation/hook/Company/useCompanyDetailVM";
import { useCompanyTariffNatureVM } from "presentation/hook/Company/useCompanyTariffNatureVM";
import { useCompanyTerminalVM } from "presentation/hook/Company/useCompanyTerminalVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useCompanyDetailTracked } from "presentation/store/Company/CompanyDetailProvider";

import { useCompanyMaintenanceTracked } from "presentation/store/Company/CompanyMaintenanceProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DatePickerComponent from "presentation/view/components/DatePickerComponent";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { FieldType, HPHCheckbox, HPHInputTextarea, IconButton, IFieldValue, InputField, Loader } from "veronica-ui-component/dist/component/core";
import { SidebarActionCross, Sidebarheader, SidebarTitle } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const CompanyInfoDetailFormPanel = () => {
    const [companyState] = useCompanyMaintenanceTracked();
    const [companyDetailState] = useCompanyDetailTracked();
    const companyDetailVM = useCompanyDetailVM();
    const companyTerminalVM = useCompanyTerminalVM()
    const companyTariffNatureVM = useCompanyTariffNatureVM();
    const messageBarVM = useMessageBarVM();
    const [anaInfoState] = useANAInfoTracked();
    const { operatingUnit, allowUpdate } = anaInfoState;

    const COMPANY_CONST = CompanyConstant.Header;
    const { currentCompany } = companyState;
    const [isLoading, setIsLoading] = useState(false);
    const { companyId, isShowRightPanel } = companyDetailState
    const { allFormState, isEditCompDtlInfo } = companyDetailState.viewState
    const { selectedTerminals } = companyDetailState.companyTerminalState
    const { selectedTariffNatures } = companyDetailState.companyTariffNatureState

    const initialData = useCallback(() => {
        companyTerminalVM.initialTerminalData(companyId);
        companyTariffNatureVM.initialTariffNatureData(companyId);
    }, [companyTariffNatureVM, companyTerminalVM, companyId])

    useEffect(() => {
        if (companyId !== 0) {
            companyDetailVM.loadDropdownOption(companyId);
            initialData();
        }
    }, [companyDetailVM, currentCompany.id, companyTerminalVM, companyTariffNatureVM, initialData, companyId]);

    const handleEdit = useCallback(() => {
        companyDetailVM.onEdit();
    }, [companyDetailVM])

    const handleReset = useCallback(() => {
        companyDetailVM.onReset();
    }, [companyDetailVM])

    const handleSaveClick = useCallback(async () => {
        if (companyId) {
            setIsLoading(true);

            await companyTerminalVM.onSave(selectedTerminals, companyId).then((res) => {
                setIsLoading(false);
            }).catch(() => {
                setIsLoading(false);
                messageBarVM.showError('Save company terminal Failed.');
                return
            });

            await companyTariffNatureVM.onSave(selectedTariffNatures, companyId).then((res) => {
                setIsLoading(false);
            }).catch(() => {
                setIsLoading(false);
                messageBarVM.showError('Save company tariff nature Failed.');
                return
            });

            initialData();
        }
    }, [companyId, companyTariffNatureVM, companyTerminalVM, initialData, messageBarVM, selectedTariffNatures, selectedTerminals])

    const memoCompanyCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <InputField
                width='185px'
                maxLength={4}
                label={COMPANY_CONST.COMPANY_CODE}
                disabled={true}
                type="text"
                value={currentCompany?.companyCode ?? ''}
                errorMessage={allFormState ? allFormState["companyCode"] : ""}
                onChange={() => { }}
                allowedSpecialChars={'all'} />
        </div>
        , [COMPANY_CONST.COMPANY_CODE, allFormState, currentCompany?.companyCode])

    const memoCompanyName = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "405px", marginBottom: "24px" }}>
            <InputField
                width='405px'
                maxLength={60}
                label={COMPANY_CONST.COMPANY_NAME}
                disabled={true}
                type="text"
                value={currentCompany?.companyName ?? ''}
                onChange={() => { }}
                allowedSpecialChars={'all'} />
        </div>
        , [COMPANY_CONST.COMPANY_NAME, currentCompany?.companyName])

    const memoActiveInd = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "190px", marginBottom: "24px" }}>
            <NbisCommonField
                fieldKey={"activeInd"}
                requiredFieldList={[]}
                fieldLabel={COMPANY_CONST.ACTIVE_IND}
                fieldType={FieldType.TEXT}
                isReadOnly={true}
                onFieldChange={() => { }} />
            <HPHCheckbox
                label={''}
                checked={currentCompany?.activeInd === 'Y' ? true : false}
                disabled={true}
                onChange={() => { }} />
        </div>
        , [COMPANY_CONST.ACTIVE_IND, currentCompany?.activeInd])

    const memoCompanyType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <InputField
                width='185px'
                maxLength={30}
                label={COMPANY_CONST.COMPANY_TYPE}
                disabled={true}
                type="text"
                value={currentCompany?.companyType ?? ''}
                onChange={() => { }}
                allowedSpecialChars={'all'} />
        </div>
        , [COMPANY_CONST.COMPANY_TYPE, currentCompany?.companyType])

    const memoMasterCompanyCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "190px", marginBottom: "24px" }}>
            <InputField
                width='190px'
                maxLength={4}
                label={COMPANY_CONST.MASTER_COMPANY}
                disabled={true}
                type="text"
                value={currentCompany?.masterCompany?.companyCode ?? ''}
                onChange={() => { }}
                allowedSpecialChars={'all'} />
        </div>
        , [COMPANY_CONST.MASTER_COMPANY, currentCompany?.masterCompany?.companyCode])

    const memoAllianceCompanyCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <InputField
                width='185px'
                maxLength={4}
                label={COMPANY_CONST.ALLIANCE}
                disabled={true}
                type="text"
                value={currentCompany?.alliance?.companyCode ?? ''}
                onChange={() => { }}
                allowedSpecialChars={'all'} />
        </div>
        , [COMPANY_CONST.ALLIANCE, currentCompany?.alliance?.companyCode])

    const memoCustomerType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <InputField
                width='185px'
                maxLength={15}
                label={CompanyConstant.Detail.CUSTOMER_TYPE}
                disabled={true}
                type="text"
                value={currentCompany?.customerType ?? ''}
                onChange={() => { }}
                allowedSpecialChars={'all'} />
        </div>
        , [currentCompany?.customerType])

    const memoAddress = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "405px", marginBottom: "24px" }}>
            <HPHInputTextarea
                label={CompanyConstant.Detail.ADDRESS}
                width="405px"
                rows={3}
                disabled={true}
                resizable={false}
                helpIcon={false}
                value={((currentCompany?.address1 ?? '') + '\n' + (currentCompany?.address2 ?? '') + '\n' + (currentCompany?.address3 ?? ''))}
                onChange={() => { }}
                allowedSpecialChars={'all'} />
        </div>
        , [currentCompany?.address1, currentCompany?.address2, currentCompany?.address3])

    const memoCountryCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <InputField
                width='185px'
                maxLength={4}
                label={COMPANY_CONST.COUNTRY_CODE}
                disabled={true}
                type="text"
                value={currentCompany?.country?.countryCode ?? ''}
                onChange={() => { }}
                allowedSpecialChars={'all'} />
        </div>
        , [COMPANY_CONST.COUNTRY_CODE, currentCompany?.country?.countryCode])

    const memoSignOnDate = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <DatePickerComponent
                disabled={true}
                label={CompanyConstant.Detail.SIGN_ON_DATE}
                width="185px"
                date={currentCompany?.signOnDate ?? ''}
                fieldName="signOnDate"
                onDateChange={() => { }} />
        </div>
        , [currentCompany?.signOnDate])

    const memoContractTml = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!isEditCompDtlInfo}
                isShowOptional={true}
                isMultipleDropDown={true}
                //disableInput={true}
                readOnlyValue={selectedTerminals?.map((item) => item?.terminalCode).join(',') ?? ''}
                fieldValue={selectedTerminals?.map((item) => item?.terminalCode)}
                fieldLabel={CompanyConstant.Detail.CONTRACT_TML}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'terminalCode'}
                options={companyDetailState.dynamicOptions.contractTmlDropdownOptions}
                maxLength={60}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    companyTerminalVM.onMultipleDropdownChange(fieldKey, fieldValue && fieldValue as string[], fFullValue)} />
        </div>
        , [allFormState, companyDetailState.dynamicOptions.contractTmlDropdownOptions, companyTerminalVM, isEditCompDtlInfo, selectedTerminals])

    const memoTariffNature = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!isEditCompDtlInfo}
                isShowOptional={true}
                isMultipleDropDown={true}
                //disableInput={true}
                readOnlyValue={selectedTariffNatures?.map((item) => item?.tariffNature).join(',') ?? ''}
                fieldValue={selectedTariffNatures?.map((item) => item?.tariffNature)}
                fieldLabel={CompanyConstant.Detail.TARIFF_NATURE}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'tariffNature'}
                options={companyDetailState.dynamicOptions.tariffNatureDropdownOptions}
                maxLength={60}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    companyTariffNatureVM.onMultipleDropdownChange(fieldKey, fieldValue && fieldValue as string[], fFullValue)} />
        </div>
        , [allFormState, companyDetailState.dynamicOptions.tariffNatureDropdownOptions, companyTariffNatureVM, isEditCompDtlInfo, selectedTariffNatures])

    const memoMobilePhoneNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <InputField
                width='185px'
                maxLength={4}
                label={COMPANY_CONST.MOBILE_PHONE_NO}
                disabled={true}
                type="text"
                value={currentCompany?.mobileNo ?? ''}
                onChange={() => { }}
                allowedSpecialChars={'all'} />
        </div>
        , [COMPANY_CONST.MOBILE_PHONE_NO, currentCompany?.mobileNo])

    const memoContactPerson = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <InputField
                width='185px'
                maxLength={4}
                label={COMPANY_CONST.CONTACT_PERSON}
                disabled={true}
                type="text"
                value={currentCompany?.contactPerson ?? ''}
                onChange={() => { }}
                allowedSpecialChars={'all'} />
        </div>
        , [COMPANY_CONST.CONTACT_PERSON, currentCompany?.contactPerson])

    const memoEmailAddress = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "405px", marginBottom: "24px" }}>
            <InputField
                width='405px'
                maxLength={300}
                label={COMPANY_CONST.EMAIL_ADDRESS}
                type="text"
                disabled={true}
                value={currentCompany?.emailAddress ?? ''}
                onChange={() => { }}
                allowedSpecialChars={'all'} />
        </div>
        , [COMPANY_CONST.EMAIL_ADDRESS, currentCompany?.emailAddress])

    const memoGstNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <InputField
                width='185px'
                maxLength={4}
                label={COMPANY_CONST.GST_NO}
                disabled={true}
                type="text"
                value={currentCompany?.gstNo ?? ''}
                onChange={() => { }}
                allowedSpecialChars={'all'} />
        </div>
        , [COMPANY_CONST.GST_NO, currentCompany?.gstNo])

    const memoNtnNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <InputField
                width='185px'
                maxLength={4}
                label={COMPANY_CONST.NTN_NO}
                disabled={true}
                type="text"
                value={currentCompany?.ntnNo ?? ''}
                onChange={() => { }}
                allowedSpecialChars={'all'} />
        </div>
        , [COMPANY_CONST.NTN_NO, currentCompany?.ntnNo])

    const memoLocalAgentCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <InputField
                width='185px'
                maxLength={4}
                label={COMPANY_CONST.LOCAL_AGENT_CODE}
                disabled={true}
                type="text"
                value={currentCompany?.localAgentCode ?? ''}
                onChange={() => { }}
                allowedSpecialChars={'all'} />
        </div>
        , [COMPANY_CONST.LOCAL_AGENT_CODE, currentCompany?.localAgentCode])

    const memoLocalAgentName = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <InputField
                width='185px'
                maxLength={4}
                label={COMPANY_CONST.LOCAL_AGENT_NAME}
                disabled={true}
                type="text"
                value={currentCompany?.localAgentName ?? ''}
                onChange={() => { }}
                allowedSpecialChars={'all'} />
        </div>
        , [COMPANY_CONST.LOCAL_AGENT_NAME, currentCompany?.localAgentName])

    const memoLocalAgentNtn = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <InputField
                width='185px'
                maxLength={4}
                label={COMPANY_CONST.LOCAL_AGENT_NTN}
                disabled={true}
                type="text"
                value={currentCompany?.localAgentNtn ?? ''}
                onChange={() => { }}
                allowedSpecialChars={'all'} />
        </div>
        , [COMPANY_CONST.LOCAL_AGENT_NTN, currentCompany?.localAgentNtn])

    const memoLocalAgentStrn = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <InputField
                width='185px'
                maxLength={4}
                label={COMPANY_CONST.LOCAL_AGENT_STRN}
                disabled={true}
                type="text"
                value={currentCompany?.localAgentStrn ?? ''}
                onChange={() => { }}
                allowedSpecialChars={'all'} />
        </div>
        , [COMPANY_CONST.LOCAL_AGENT_STRN, currentCompany?.localAgentStrn])

    const memoLocalAgentAddress = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "405px", marginBottom: "24px" }}>
            <HPHInputTextarea
                label={CompanyConstant.Detail.LOCAL_AGENT_ADDRESS}
                width="405px"
                rows={3}
                disabled={true}
                resizable={false}
                helpIcon={false}
                value={((currentCompany?.localAgentAddress1 ?? '') + '\n' + (currentCompany?.localAgentAddress2 ?? '') + '\n' + (currentCompany?.localAgentAddress3 ?? ''))}
                onChange={() => { }} />
        </div>
        , [currentCompany?.localAgentAddress1, currentCompany?.localAgentAddress2, currentCompany?.localAgentAddress3])

    const memoAgenNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <InputField
                width='185px'
                maxLength={4}
                label={COMPANY_CONST.AGENT_NO}
                disabled={true}
                type="text"
                value={currentCompany?.agentNo ?? ''}
                errorMessage={allFormState ? allFormState["agentNo"] : ""}
                onChange={() => { }}
                allowedSpecialChars={'all'} />
        </div>
        , [COMPANY_CONST.AGENT_NO, allFormState, currentCompany?.agentNo])

    const memoAgenName = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <InputField
                width='185px'
                maxLength={4}
                label={COMPANY_CONST.AGENT_NAME}
                disabled={true}
                type="text"
                value={currentCompany?.agentName ?? ''}
                errorMessage={allFormState ? allFormState["agentName"] : ""}
                onChange={() => { }}
                allowedSpecialChars={'all'} />
        </div>
        , [COMPANY_CONST.AGENT_NAME, allFormState, currentCompany?.agentName])

    const memoLetPassAccount = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <InputField
                width='185px'
                maxLength={4}
                label={COMPANY_CONST.LET_PASS_ACCOUNT}
                disabled={true}
                type="text"
                value={currentCompany?.letPassAccountInd ?? ''}
                errorMessage={allFormState ? allFormState["letPassAccountInd"] : ""}
                onChange={() => { }}
                allowedSpecialChars={'all'} />
        </div>
        , [COMPANY_CONST.LET_PASS_ACCOUNT, allFormState, currentCompany?.letPassAccountInd])

    const memoLetPassAccountBalance = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px" }}>
            <InputField
                width='185px'
                maxLength={4}
                label={COMPANY_CONST.LET_PASS_ACCOUNT_BALANCE}
                disabled={true}
                type="text"
                value={currentCompany?.lapBalance ?? ''}
                errorMessage={allFormState ? allFormState["lapBalance"] : ""}
                onChange={() => { }}
                allowedSpecialChars={'all'} />
        </div>
        , [COMPANY_CONST.LET_PASS_ACCOUNT_BALANCE, allFormState, currentCompany?.lapBalance])

    return (
        <div className='side-form-content-left-wrapper'>
            <div className={'flex-row'}>
                <div className={'flex-row-item flex-row-item-full-width'}>
                    <Sidebarheader>
                        <SidebarTitle>{currentCompany?.companyCode ?? ''}</SidebarTitle>
                        <SidebarActionCross>
                            {
                                <>
                                    {
                                        (
                                            allowUpdate && (!isEditCompDtlInfo)
                                        )
                                        && <IconButton fileName='Icon-pen' size='medium' disabled={isEditCompDtlInfo} toolTipText={'Edit'} toolTipArrow={false} onClick={handleEdit} />
                                    }
                                </>}

                            {(isEditCompDtlInfo) &&
                                <>
                                    {<IconButton fileName="Icon-cross" toolTipText="Cancel" disabled={!isEditCompDtlInfo || isShowRightPanel} toolTipArrow={false} toolTipPlacement="bottom" size="medium" onClick={handleReset} />
                                    }
                                    {
                                        <IconButton fileName="Icon-tick" size="medium" disabled={!isEditCompDtlInfo || isShowRightPanel} toolTipArrow={false} toolTipPlacement="bottom" toolTipText={'Save'} onClick={handleSaveClick} />
                                    }
                                </>}
                        </SidebarActionCross>
                    </Sidebarheader>
                </div>
            </div>

            {isLoading && <Loader Indicator="Spinner" size="Medium" />}

            <div className={'add-edit-form'} style={{ height: '75vh', maxHeight: '75vh', overflow: 'auto' }}>
                <div className="flex-row-auto">
                    <div className={'flex-row-item'}>
                        <CriteriaItemContainer>
                            {memoCompanyType}
                            {memoActiveInd}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoCompanyName}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoCompanyCode}
                            {memoMasterCompanyCode}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoAllianceCompanyCode}
                            {memoCustomerType}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoAddress}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoSignOnDate}
                            {memoCountryCode}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoMobilePhoneNo}
                            {memoContactPerson}
                        </CriteriaItemContainer>
                        <CriteriaItemContainer>
                            {memoEmailAddress}
                        </CriteriaItemContainer>
                        <CriteriaItemContainer>
                            {memoGstNo}
                            {memoNtnNo}
                        </CriteriaItemContainer>
                        <CriteriaItemContainer>
                            {memoContractTml}
                            {memoTariffNature}
                        </CriteriaItemContainer>
                        <CriteriaItemContainer>
                            {memoAgenNo}
                            {memoAgenName}
                        </CriteriaItemContainer>
                        <CriteriaItemContainer>
                            {memoLetPassAccount}
                            {memoLetPassAccountBalance}
                        </CriteriaItemContainer>
                        {(operatingUnit === 'SAPT' || operatingUnit === 'KICT') && <>
                            <CriteriaItemContainer>
                                {memoLocalAgentCode}
                                {memoLocalAgentName}
                            </CriteriaItemContainer>
                            <CriteriaItemContainer>
                                {memoLocalAgentNtn}
                                {memoLocalAgentStrn}
                            </CriteriaItemContainer>
                            <CriteriaItemContainer>
                                {memoLocalAgentAddress}
                            </CriteriaItemContainer>
                        </>}
                    </div>
                </div>
            </div>

            <div className='im-charge-data-search-criteria-buttons'>
                {/*
                    <>
                        <HPHButton label={'Cancel'} size={'Small'} theme={'Secondary'} onClick={()=>handleCancelClick()} />
                        <HPHButton label={isAdd?'Add':isEditable?'Update':'Save'} size={'Small'} theme={'Primary'} onClick={()=>handleSaveClick()} />
                    </>
                */}
            </div>

        </div>
    )
}
export default memo(CompanyInfoDetailFormPanel);